import React, { type ReactNode } from 'react';
import BoardIcon from '@atlaskit/icon/glyph/board';
import SprintIcon from '@atlaskit/icon/glyph/sprint';
import type { MessageDescriptor } from '@atlassian/jira-intl';
import Kanban from '../../assets/Kanban.svg';
import Scrum from '../../assets/Scrum.svg';
import type { ImportType, SupportedThirdParty } from '../import-type';
import { type TemplateType, TemplateTypes } from '../project';
import messages from './messages';

export const SELECT_SOURCE_STEP = 'SELECT_SOURCE_STEP';
export const MAPPING_STEP = 'MAPPING_STEP';
export const THIRD_PARTY_REVIEW_STEP = 'THIRD_PARTY_REVIEW_STEP';
export const PROJECT_CREATE_STEP = 'PROJECT_CREATE_STEP';
export const IMPORT_IN_PROGRESS_STEP = 'IMPORT_IN_PROGRESS_STEP';
export const SELECT_THIRD_PARTY_ENTITY_STEP = 'SELECT_THIRD_PARTY_ENTITY_STEP';
export const USER_MAPPING_STEP = 'USER_MAPPING_STEP';
export const CSV_USER_MAPPING_STEP = 'CSV_USER_MAPPING_STEP';
export const UPLOAD_CSV_STANDARD_STEP = 'UPLOAD_CSV_STANDARD_STEP';
export const REVIEW_SCREEN_STANDARD_STEP = 'REVIEW_SCREEN_STANDARD_STEP';

export const fullScreenSteps: Step[] = [
	UPLOAD_CSV_STANDARD_STEP,
	PROJECT_CREATE_STEP,
	MAPPING_STEP,
	CSV_USER_MAPPING_STEP,
	REVIEW_SCREEN_STANDARD_STEP,
	IMPORT_IN_PROGRESS_STEP,
	SELECT_THIRD_PARTY_ENTITY_STEP,
	USER_MAPPING_STEP,
	THIRD_PARTY_REVIEW_STEP,
];

export const ReviewTilesType = {
	FIELDS: 'FIELDS',
	ATTACHMENTS: 'ATTACHMENTS',
	HIERARCHY: 'HIERARCHY',
	USERS: 'USERS',
	COMMENTS: 'COMMENTS',
	STATUS: 'STATUS',
} as const;

export type ReviewTilesType = (typeof ReviewTilesType)[keyof typeof ReviewTilesType];

export const CSVReviewTilesType = {
	PROJECT_DETAILS: 'PROJECT_DETAILS',
	FIELDS: 'FIELDS',
	USERS: 'USERS',
	ISSUES: 'ISSUES',
} as const;

export type CSVReviewTilesType = (typeof CSVReviewTilesType)[keyof typeof CSVReviewTilesType];

export const StatisticsType = {
	TASKS: 'TASKS',
	MEMBERS: 'MEMBERS',
} as const;

export type StatisticsType = (typeof StatisticsType)[keyof typeof StatisticsType];

export type Step =
	| typeof SELECT_SOURCE_STEP
	| typeof MAPPING_STEP
	| typeof PROJECT_CREATE_STEP
	| typeof IMPORT_IN_PROGRESS_STEP
	| typeof SELECT_THIRD_PARTY_ENTITY_STEP
	| typeof USER_MAPPING_STEP
	| typeof CSV_USER_MAPPING_STEP
	| typeof THIRD_PARTY_REVIEW_STEP
	| typeof UPLOAD_CSV_STANDARD_STEP
	| typeof REVIEW_SCREEN_STANDARD_STEP;

type OverRides = {
	overrideReturnStep?: Step;
	overrideNextStep?: Step;
};

type StepProps = {
	hasProgressBar: boolean;
};

export type Template = {
	key: TemplateType;
	title: MessageDescriptor;
	description: MessageDescriptor;
	iconUrl: string;
	icon: ReactNode;
	isDefault?: boolean;
};

type SelectSourceComponent = {
	step: typeof SELECT_SOURCE_STEP;
	overrides: OverRides;
	stepProps: {};
	progressBarStepLabel?: MessageDescriptor;
};

type UploadCSVCompactComponent = {
	step: typeof UPLOAD_CSV_STANDARD_STEP;
	overrides: OverRides;
	stepProps: {
		isCompact?: boolean;
		importType?: ImportType;
	};
	progressBarStepLabel?: MessageDescriptor;
};

type SelectThirdPartyEntityComponent = {
	step: typeof SELECT_THIRD_PARTY_ENTITY_STEP;
	overrides: OverRides;
	stepProps: {
		thirdParty: SupportedThirdParty;
		stats: StatisticsType[];
		hasSearchableEntities?: boolean;
		isMultipleEntitiesSupported?: boolean;
		shouldShowPlaceholderIcons?: boolean;
		shouldShowFolderSelector?: boolean;
		hideWorkspaceSelector?: boolean;
		shouldShowSpaceSelector?: boolean;
		shouldShowAccountInfoIfUsingPAT?: boolean;
	};
	progressBarStepLabel?: MessageDescriptor;
};

type ImportInProgressComponent = {
	step: typeof IMPORT_IN_PROGRESS_STEP;
	overrides: OverRides;
	stepProps: StepProps & {
		thirdParty?: SupportedThirdParty;
		hasImportUsers?: boolean;
		showCrossButton?: boolean;
		isCompact?: boolean;
	};
	progressBarStepLabel?: MessageDescriptor;
};

type ProjectCreateComponent = {
	step: typeof PROJECT_CREATE_STEP;
	overrides: OverRides;
	stepProps: StepProps & {
		shouldPrefillThirdPartyProjectName: boolean;
		shouldShowThirdPartyStats: boolean;
		thirdParty?: SupportedThirdParty;
		shouldStartImportOnNext: boolean;
		isTMPSupported: boolean;
		templates: Template[];
		importType?: ImportType;
	};
	progressBarStepLabel?: MessageDescriptor;
};

type MappingComponent = {
	step: typeof MAPPING_STEP;
	overrides: OverRides;
	stepProps: {
		shouldStartImportOnNext: boolean;
		showLearnMoreLink?: boolean;
		isCompact?: boolean;
		hideFeedback?: boolean;
		rightAlignFooter?: boolean;
		offsetModalY?: number;
	};
	progressBarStepLabel?: MessageDescriptor;
};

type UserMappingComponent = {
	step: typeof USER_MAPPING_STEP;
	overrides: OverRides;
	stepProps: StepProps & {
		thirdParty: SupportedThirdParty;
	};
	progressBarStepLabel?: MessageDescriptor;
};

type CSVUserMappingComponent = {
	step: typeof CSV_USER_MAPPING_STEP;
	overrides: OverRides;
	stepProps: {
		isCompact?: boolean;
		importType?: ImportType;
	};
	progressBarStepLabel?: MessageDescriptor;
};

type ThirdPartyReviewComponent = {
	step: typeof THIRD_PARTY_REVIEW_STEP;
	overrides: OverRides;
	stepProps: {
		thirdParty: SupportedThirdParty;
		tiles: ReviewTilesType[];
		isMultipleEntitiesSupported?: boolean;
	};
	progressBarStepLabel?: MessageDescriptor;
};

type ReviewScreenStandardComponent = {
	step: typeof REVIEW_SCREEN_STANDARD_STEP;
	overrides: OverRides;
	stepProps: {
		isCompact?: boolean;
		tiles: CSVReviewTilesType[];
		shouldStartImportOnNext: boolean;
	};
	progressBarStepLabel?: MessageDescriptor;
};

export type StepComponent =
	| SelectSourceComponent
	| SelectThirdPartyEntityComponent
	| ImportInProgressComponent
	| ProjectCreateComponent
	| MappingComponent
	| UserMappingComponent
	| CSVUserMappingComponent
	| ThirdPartyReviewComponent
	| UploadCSVCompactComponent
	| ReviewScreenStandardComponent;

export const DEFAULT_CONFIG = {
	overrides: {},
	stepProps: {},
};

export const templatesJSW: Template[] = [
	{
		key: TemplateTypes.KANBAN,
		title: messages.kanbanTemplateTitle,
		description: messages.kanbanTemplateDescription,
		iconUrl: Kanban,
		isDefault: true,
		icon: <BoardIcon label="Kanban" />,
	},
	{
		key: TemplateTypes.SCRUM,
		title: messages.scrumTemplateTitle,
		description: messages.scrumTemplateDescription,
		iconUrl: Scrum,
		icon: <SprintIcon label="Scrum" />,
	},
];
